import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const MIN_TITLE_FONT_SIZE = 13;
const MAX_TITLE_FONT_SIZE = 24;

function FittedTitle({ children }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(MAX_TITLE_FONT_SIZE);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;

    if (!container || !text) return undefined;

    const fitText = () => {
      const availableWidth = container.clientWidth;
      if (!availableWidth) return;

      const previousFontSize = text.style.fontSize;
      text.style.fontSize = `${MAX_TITLE_FONT_SIZE}px`;
      const naturalWidth = text.scrollWidth;
      text.style.fontSize = previousFontSize;

      if (!naturalWidth) return;

      const nextFontSize = Math.max(
        MIN_TITLE_FONT_SIZE,
        Math.min(
          MAX_TITLE_FONT_SIZE,
          Math.floor((availableWidth / naturalWidth) * MAX_TITLE_FONT_SIZE),
        ),
      );

      setFontSize(nextFontSize);
    };

    fitText();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", fitText);
      return () => window.removeEventListener("resize", fitText);
    }

    const observer = new ResizeObserver(fitText);
    observer.observe(container);
    return () => observer.disconnect();
  }, [children]);

  return (
    <span ref={containerRef} className="block w-full min-w-0">
      <span
        ref={textRef}
        className="block w-full whitespace-nowrap text-center font-semibold leading-tight text-greyscale-1"
        style={{ fontSize: `${fontSize}px` }}
      >
        {children}
      </span>
    </span>
  );
}

export default function ModeTileSmall({
  to,
  title,
  desc,
  img = null,
  onClick,
  disabled = false,
}) {
  const { t } = useTranslation();

  const baseClass = `relative primary-button h-full min-h-16 w-full min-w-0 flex items-center justify-center ${
    disabled ? "opacity-70 pointer-events-none" : ""
  }`;

  const inner = (
    <>
      {img ? (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none text-greyscale-1 fill-greyscale-1">
          {img}
        </span>
      ) : null}

      {/* Reserve space for icon on BOTH sides so centered text stays centered */}
      <div className="w-full min-w-0 px-9 flex flex-col items-center text-center">
        <FittedTitle>{title}</FittedTitle>
        {desc ? <p className="text-sm text-greyscale-2">{desc}</p> : null}
        {disabled ? (
          <div className="opacity-60">
            {t("levelSelection.notSupportedCta")}
          </div>
        ) : null}
      </div>
    </>
  );

  if (disabled) {
    return (
      <button type="button" className={baseClass} disabled>
        {inner}
      </button>
    );
  }

  if (!to) {
    return (
      <button type="button" className={baseClass} onClick={onClick}>
        {inner}
      </button>
    );
  }

  return (
    <Link to={to} className={baseClass} onClick={onClick}>
      {inner}
    </Link>
  );
}
